import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction';
import {useState, useEffect, useContext} from 'react'
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Button, Dialog, TextField,
    DialogActions, DialogContent, DialogTitle } from '@mui/material';
import moment from 'moment-timezone';
import './Calendar.css'; 
import { ApiContext } from '../App';

export default function Calendar({t, facility}) {

    const [events, setEvents] = useState([])
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogData, setDialogData] = useState({})
    const [mondayDate, setMondayDate] = useState("")
    const [clientInfo, setClientInfo] = useState({})
    const {cogenDemandApi, clientApi} = useContext(ApiContext)

   useEffect(() => {
        clientApi.getClient((data) => {
            setClientInfo(data)
        },
            () => {}
        )
    }, [clientApi])

    useEffect(() => {
        cogenDemandApi.list(facility, (data) => {
            setEvents(data.map((e) => formatEvent(e)))
        }, () => {
            alert("Error fetching demand")
        })
    }, [facility, clientInfo])

    
    const handleEventClick = (event) => {
        setDialogOpen(true)
        setDialogData({
            start: event.event.startStr,
            end: event.event.endStr,
            value: events.find((e) => e.id ===event.event.id ).value,
            facility: facility,
            id: event.event.id
        })
    }


    const formatEvent = (event) => {
        return {
            start: moment.tz(event.start, clientInfo.timezone).format(),
            end: moment.tz(event.end, clientInfo.timezone).format(),
            value: event.value,
            title: event.value + " MW",
            id: event.url,
            facility: event.facility
        }
    }
    

    const eventHandler = (event, delta, revertFunc) => {
        let newEvent = events.find((item) => item.id === event.event.id)
        newEvent.start = event.event.startStr
        newEvent.end = event.event.endStr
        cogenDemandApi.update(event.event.id, newEvent, () => {
            const newEvents = events.map((e) => 
                (e.id === event.event.id) ? {
                    ...e, 
                    start: event.event.startStr,
                    end: event.event.endStr
                } : e
            )
            setEvents(newEvents)
        }, () => {
            alert("Couldn't update demand")
        })
        
    }   

    const handleSelect = (selectInfo) => {
        setDialogOpen(true)
        setDialogData({
            start: selectInfo.startStr,
            end: selectInfo.endStr,
            value: "0.0",
            facility: facility,
            id: undefined
        })
    }

    const handleValueChange = (id, value) => {
        if (events.find((e)=>e.id === id)) {

            setEvents(
                events.map((e) => e.id === id ? {
                    ...e,
                    value,
                    title: value + " MW"
                } : e)
            )
        } else {
            setEvents([...events, {
                start: dialogData.start,
                end: dialogData.end,
                facility,
                value,
                title: value + " MW",
                id: id
            }])

        }

    }

    const handleDelete = (id) => {
        setEvents(
            events.filter((e)=>e.id !== id)
        )

    }

    const copyHandler = () => {
        cogenDemandApi.copyPreviousDemandWeek(mondayDate, facility, () => {
            cogenDemandApi.list(facility, (data) => {
                setEvents(data.map((e) => formatEvent(e)))
            }, () => {
                alert("Error fetching demand")
            })
        }, () => {
            alert(t('Error copying week'))
        })
    }

    const dateHandler = (dateInfo) => {
        setMondayDate(dateInfo.startStr.split('T')[0])
    }


    return (
        <Box sx={{padding: "10px"}} height='80%'>
            <EditDialog 
                dialogOpen={dialogOpen} 
                onDialogClose={()=> setDialogOpen(false)} 
                onValueChange={handleValueChange} 
                onDelete={handleDelete} 
                t={t} 
                start={dialogData.start} 
                end={dialogData.end} 
                value={dialogData.value} 
                facility={dialogData.facility} 
                id={dialogData.id} 
                cogenDemandApi={cogenDemandApi}
                timezone={clientInfo.timezone}
                />
            <FullCalendar 
                plugins={[ dayGridPlugin, timeGridPlugin, interactionPlugin ]}
                initialView="timeGridWeek"
                slotDuration='01:00'
                editable={true}
                selectable={true}
                select={handleSelect}
                initialDate={new Date()}  
                events={events}
                eventClick={handleEventClick}
                eventChange={eventHandler}
                eventOverlap={false}
                contentHeight={"685px"}
                weekNumberCalculation={"ISO"}
                datesSet={dateHandler}
                timeZone={clientInfo.timezone}
            />
            <Button onClick={copyHandler} >
                Copy from previous week
            </Button>
        </Box>
    )
}

const EditDialog = ({ dialogOpen, onDialogClose, t, onDelete, start, end, value, id, facility, onValueChange, cogenDemandApi, timezone }) => {
    const [dialogValue, setDialogValue] = useState(value ?? "")
    useEffect(() => {
        setDialogValue(value)
        
        }
    , [value])

    const onSave = (event) => {
        if (id) {
            const payload = {
                start: start,
                end: end,
                value: dialogValue,
                facility: facility
            }

            cogenDemandApi.update(id, payload, () => {
                
                onDialogClose()
                onValueChange(id, dialogValue)
            }, () => {
                alert("Couldn't update demand")
            })
        } else {
            const payload = {
                start: moment.tz(start, timezone).utc().format(),
                end: moment.tz(end, timezone).utc().format(),
                value: dialogValue,
                facility: facility
            }

            cogenDemandApi.create(payload, (data) => {
                onDialogClose()
                onValueChange(data.url, dialogValue)
            }, () => {
                alert("Couldn't update demand")
            })
        }
    }   
    

    const handleDelete = () => {

        cogenDemandApi.delete(id, () => {
                
            onDialogClose()
            onDelete(id)
        }, () => {
            alert("Couldn't delete demand")
        })

        
    }


    return (
        <Dialog open={dialogOpen} onClose={onDialogClose} >
      <DialogTitle>
      {start?.split("T")[0]} {start?.split("T")[1].split("+")[0]} - {end?.split("T")[1].split("+")[0]}
      </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
          <TextField
                        variant="standard"
                        id="value"
                        label={t("Value per hour (MW)")}
                        type="number"
                        value={dialogValue}
                        onChange={(e)=>setDialogValue(e.target.value)}
                />
          </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave} autoFocus>{t("Save")}</Button>
        <Button onClick={handleDelete} >{t("Delete")}</Button>
        <Button onClick={onDialogClose} >{t("Cancel")}</Button>
      </DialogActions>
    </Dialog>
    )
}